import { GVToggleButton } from 'components/common';
import CropIcon from '@mui/icons-material/Crop';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { useSelector } from 'react-redux';
import { getAllAnnotationsLoaded, getFilesMetadata, getIsSingleFile, getSelectedTool } from 'store';
import { makeStyles } from 'tss-react/mui';
import { DocumentTypes, PdfBoxType, PDFTool, PDFTRON_DEFAULT_TOOL, PDFTronTools } from 'types';
import { useState, MouseEvent } from 'react';
import { Box, Button, IconButton, ListItem, ListItemButton, Menu, MenuList, Theme } from '@mui/material';
import { PDFManagerFactory } from 'pdftron';
import BleedBoxIcon from 'components/icons/PDFBoxes/BleedBoxIcon';
import TrimBoxIcon from 'components/icons/PDFBoxes/TrimBoxIcon';
import ArtBoxIcon from 'components/icons/PDFBoxes/ArtBoxIcon';
import PolygonToolIcon from 'components/icons/PolygonToolIcon/PolygonToolIcon';
import PointerBoundary from './PointerBoundary';

const useStyles = makeStyles()((theme: Theme) => {
  return {
    toggleButton: {
      padding: '0px',
      height: '48px',
      width: '48px',
    },
    toggleButtonBorderRadius: {
      borderRightWidth: '0px',
      borderTopRightRadius: '0px',
      borderBottomRightRadius: '0px',
    },
    toggleIcon: { fontSize: '30px', padding: '0', color: 'white' },
    arrowButton: {
      minWidth: '25px',
      paddingRight: '0px',
      paddingLeft: '0px',
      borderTopLeftRadius: '0px',
      borderBottomLeftRadius: '0px',
      height: '48px',
    },
    arrowButtonSelected: {
      backgroundColor: '#EA3632',
      borderLeft: '1px solid black',
      '&.MuiButtonBase-root': {
        '&:hover': {
          backgroundColor: 'rgba(234, 54, 50, 0.85)',
        },
      },
    },
    arrowIcon: {
      transform: 'rotate(0deg)',
      transition: 'transform 0.3s ease',
    },
    arrowIconSelected: {
      transform: 'rotate(180deg)',
      transition: 'transform 0.3s ease',
    },
    menuList: {
      padding: '16px !important',
    },
    zoneIcon: {
      color: 'white',
    },
    listItem: {
      padding: '8px 16px',
    },
    iconButton: {
      padding: 0,
      ' .MuiBox-root': {
        fontSize: '12px',
        fontWeight: '600',
        lineHeight: '17px',
        color: 'white',
      },
    },
    sliderButton: {
      zIndex: 1,
      flex: 1,
      opacity: 1,
      color: '#FFFFFF',
      fontSize: '10px',
      fontWeight: '600',
      minWidth: 'initial',
    },
    dot: {
      height: '4px',
      width: '4px',
      marginLeft: `-${theme.spacing(1.25)}`,
    },
    polygonIcon: {
      marginLeft: `${theme.spacing(0.75)}`, // Compensate for the dot width
    },
  };
});

interface CropToolProps {
  handleSelectTool: (tool: PDFTool) => void;
}
const CropTool = ({ handleSelectTool }: CropToolProps) => {
  const { classes, cx } = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const annotationLoaded = useSelector(getAllAnnotationsLoaded);
  const selectedTool = useSelector(getSelectedTool);
  const isSingleFile = useSelector(getIsSingleFile);
  const sideMenuOpen = Boolean(anchorEl);
  const isCropOrPolygonActive = selectedTool === PDFTronTools.CROP || selectedTool === PDFTronTools.POLYGON;
  const [selectedDocument, setSelectedDocument] = useState(DocumentTypes.source);
  const { masterMetadata, sampleMetadata } = useSelector(getFilesMetadata);

  const masterPdfBoxes = masterMetadata?.pdfBoxes || {};
  const samplePdfBoxes = sampleMetadata?.pdfBoxes || {};

  const boxTypes = [
    { type: PdfBoxType.bleedBox, icon: <BleedBoxIcon />, label: 'BleedBox' },
    { type: PdfBoxType.trimBox, icon: <TrimBoxIcon />, label: 'TrimBox' },
    { type: PdfBoxType.artBox, icon: <ArtBoxIcon />, label: 'ArtBox' },
  ];

  const checkPdfBoxes = (pdfBoxes: any) => ({
    hasArtBox: PdfBoxType.artBox in pdfBoxes,
    hasTrimBox: PdfBoxType.trimBox in pdfBoxes,
    hasBleedBox: PdfBoxType.bleedBox in pdfBoxes,
  });

  const masterBoxInfo = checkPdfBoxes(masterPdfBoxes);
  const sampleBoxInfo = checkPdfBoxes(samplePdfBoxes);

  const handleDocumentToggle = (option: React.SetStateAction<DocumentTypes>) => {
    setSelectedDocument(option);
  };

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (!isCropOrPolygonActive) handleSelectTool(PDFTronTools.CROP);
    if (isSingleFile) setSelectedDocument(DocumentTypes.target);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const applyCrop = (pdfBoxType: PdfBoxType) => {
    const docManager = PDFManagerFactory.getPDFDocManager();
    const pdfBoxes =
      selectedDocument === DocumentTypes.source ? masterPdfBoxes[pdfBoxType] : samplePdfBoxes[pdfBoxType];
    if (pdfBoxes) {
      docManager?.applyPDFBox(selectedDocument, pdfBoxes);
    }
    if (selectedTool !== PDFTronTools.CROP) {
      handleSelectTool(PDFTronTools.CROP);
    }
    setAnchorEl(null);
  };

  const isBoxDisabled = (boxType: PdfBoxType) => {
    const boxInfo = selectedDocument === DocumentTypes.source ? masterBoxInfo : sampleBoxInfo;

    switch (boxType) {
      case PdfBoxType.artBox:
        return !boxInfo.hasArtBox;
      case PdfBoxType.trimBox:
        return !boxInfo.hasTrimBox;
      case PdfBoxType.bleedBox:
        return !boxInfo.hasBleedBox;
      default:
        return true; // Default to disabled if an unknown box type is passed
    }
  };

  return (
    <div>
      {/* div is used to apply the parent gap to the button and dropdown menu gap as a group */}
      <GVTooltip title={'Crop'} side="bottom">
        <PointerBoundary>
          <GVToggleButton
            data-testid="markup_tools_crop"
            value={PDFTronTools.CROP}
            id="zoneArea"
            disabled={!annotationLoaded}
            selected={isCropOrPolygonActive}
            onClick={() => {
              handleSelectTool(selectedTool === PDFTronTools.POLYGON ? PDFTRON_DEFAULT_TOOL : PDFTronTools.CROP);
            }}
            className={cx(classes.toggleButton, { [classes.toggleButtonBorderRadius]: !isSingleFile })}
          >
            <CropIcon color="primary" fontSize="inherit" className={classes.toggleIcon} />
          </GVToggleButton>
        </PointerBoundary>
      </GVTooltip>
      <GVTooltip title={`More crop options`} side="top">
        <PointerBoundary>
          <Button
            className={cx(classes.arrowButton, {
              [classes.arrowButtonSelected]: sideMenuOpen || isCropOrPolygonActive,
            })}
            id={`crop-more-options`}
            color="primary"
            size="small"
            onClick={handleOpenMenu}
            variant="outlined"
            disabled={!annotationLoaded}
          >
            <ArrowDropDownIcon
              className={cx({
                [classes.arrowIconSelected]: sideMenuOpen || isCropOrPolygonActive,
                [classes.arrowIcon]: !sideMenuOpen,
              })}
            />
          </Button>
        </PointerBoundary>
      </GVTooltip>
      <Menu
        anchorEl={anchorEl}
        open={sideMenuOpen}
        onClose={handleClose}
        className={classes.menuList}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuList
          dense
          sx={{
            paddingBottom: '0 !important',
            ...(isSingleFile && { paddingTop: '0 !important' }),
          }}
        >
          {!isSingleFile && (
            <ListItem className={classes.listItem} sx={{ paddingTop: '0 !important' }}>
              <Box display="flex" alignItems="center">
                <Box sx={{ color: 'rgba(255, 255, 255, 0.38)', fontSize: '12px', fontWeight: '400' }} mr={1}>
                  CROP ON
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    position: 'relative',
                    borderRadius: '3.75px',
                    overflow: 'hidden',
                    backgroundColor: 'var(--gds-color-cool-gray-900, #101113)',
                    width: '100px',
                    height: '20px',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 1,
                      left: selectedDocument === DocumentTypes.source ? 0 : '50%',
                      width: '50%',
                      height: '17.5px',
                      backgroundColor: '#EA3632',
                      transition: 'left 0.3s ease',
                      padding: '2.5px 5px',
                      borderRadius: '3.75px',
                    }}
                  />
                  <Button className={classes.sliderButton} onClick={() => handleDocumentToggle(DocumentTypes.source)}>
                    Source
                  </Button>

                  <Button className={classes.sliderButton} onClick={() => handleDocumentToggle(DocumentTypes.target)}>
                    New
                  </Button>
                </Box>
              </Box>
            </ListItem>
          )}
          {boxTypes.map((boxType) => (
            <ListItemButton
              key={boxType.type}
              className={classes.listItem}
              disabled={isBoxDisabled(boxType.type)}
              onClick={() => applyCrop(boxType.type)}
            >
              <IconButton className={classes.iconButton}>
                <Box display="flex" alignItems="center">
                  {boxType.icon}
                  <Box ml={'10px'}>{boxType.label}</Box>
                </Box>
              </IconButton>
            </ListItemButton>
          ))}
          <ListItemButton
            className={classes.listItem}
            onClick={() => {
              handleSelectTool(PDFTronTools.POLYGON);
              handleClose();
            }}
          >
            <IconButton className={classes.iconButton}>
              <Box display="flex" alignItems="center">
                <div
                  className={classes.dot}
                  style={{ backgroundColor: selectedTool === PDFTronTools.POLYGON ? '#6DDAE2' : 'transparent' }}
                />
                <PolygonToolIcon className={classes.polygonIcon} />
                <Box ml={'10px'}>Polygon</Box>
              </Box>
            </IconButton>
          </ListItemButton>
        </MenuList>
      </Menu>
    </div>
  );
};

export default CropTool;
